import type { ApiComponents as components } from '@aztec-realms/api-client';
import { browserApiClient, requiredApiData } from './browser-api';

export type PrivacyStatus = components['schemas']['PrivacyStatusResponse'];
export type PrivacyCollection = components['schemas']['PrivacyExportRequest']['collection'];

export async function fetchPrivacyStatus(signal?: AbortSignal): Promise<PrivacyStatus> {
  const { data, error } = await browserApiClient().GET('/api/v1/account/privacy/status', {
    signal,
  });
  return requiredApiData(data, error, 'Your privacy options could not be loaded.');
}

export async function exportPrivacyCollection(
  collection: PrivacyCollection,
  password: string,
  signal: AbortSignal,
) {
  const pages: components['schemas']['PrivacyExportResponse'][] = [];
  let offset = 0;
  let bytes = 0;
  for (let count = 0; count < 1001; count += 1) {
    signal.throwIfAborted();
    const { data, error } = await browserApiClient({ mutation: true }).POST(
      '/api/v1/account/privacy/export',
      {
        body: { current_password: password, collection, offset, limit: 100 },
        signal,
      },
    );
    const page = requiredApiData(data, error, 'The download was interrupted. Please try again.');
    bytes += new TextEncoder().encode(JSON.stringify(page)).length;
    if (bytes > 20 * 1024 * 1024)
      throw new Error('This collection needs an assisted export. No partial download was saved.');
    pages.push(page);
    if (page.next_offset === null) {
      signal.throwIfAborted();
      return { collection, complete: true, pages };
    }
    if (page.next_offset <= offset)
      throw new Error('The download could not finish. No partial download was saved.');
    offset = page.next_offset;
  }
  throw new Error('This collection needs an assisted export. No partial download was saved.');
}

export async function requestAccountErasure(password: string, signal: AbortSignal) {
  const { data, error } = await browserApiClient({ mutation: true }).POST(
    '/api/v1/account/delete',
    {
      body: { current_password: password },
      signal,
    },
  );
  return requiredApiData(data, error, 'Your request could not be confirmed.');
}
